'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface DeviceState {
  isMobile: boolean;    // < 768px
  isTablet: boolean;    // 768px - 1023px
  isDesktop: boolean;   // >= 1024px
  deviceDetected: boolean;
}

const DeviceContext = createContext<DeviceState>({
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  deviceDetected: false,
});

export function useDevice(): DeviceState {
  return useContext(DeviceContext);
}

export function DeviceProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DeviceState>({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    deviceDetected: false,
  });

  useEffect(() => {
    const detect = () => {
      const w = window.innerWidth;
      setState({
        isMobile: w < 768,
        isTablet: w >= 768 && w < 1024,
        isDesktop: w >= 1024,
        deviceDetected: true,
      });
    };

    detect();

    let timer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(detect, 150);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  return (
    <DeviceContext.Provider value={state}>
      {children}
    </DeviceContext.Provider>
  );
}
